import React, { useState, useEffect } from 'react';
import { FaShoppingCart, FaHeart as EmptyHeart } from 'react-icons/fa';
import { FaHeart as FilledHeart } from 'react-icons/fa6';
import { MdOutlineLocalShipping } from "react-icons/md";
import { BsArrowRepeat } from "react-icons/bs";
import slugify from 'slugify';
import { useNavigate } from 'react-router-dom';

const appleFont = {
  fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif"
};

const ImagePlaceholder = () => (
  <div className="w-full h-full animate-pulse bg-gray-100" />
);

const ProductCard = ({ product, addToCart, toggleFavorite, isFavorite, cartItems = [], navigate }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
    if (product.image) {
      const img = new Image();
      img.src = product.image;
      img.onload = () => setImageLoaded(true);
      img.onerror = () => setImageError(true);
    }
  }, [product]);

  const inCart = cartItems?.some(item => item.id === (product.productId || product.id)) || false;
  const isOutOfStock = product.stock === 0;

  const handleAddToCartClick = (e) => { e.stopPropagation(); addToCart(product); };
  const handleCardClick = () => {
    const titleSlug = slugify(product.name, { lower: true, strict: true, trim: true });
    if (navigate) navigate(`/product/${product.id}/${titleSlug}`);
  };
  const handleFavoriteClick = (e) => { e.stopPropagation(); toggleFavorite(product.id); };

  return (
    <div
      onClick={handleCardClick}
      style={appleFont}
      className="bg-white cursor-pointer flex flex-col h-full relative group hover:shadow-md transition-shadow duration-200"
    >
      {/* Favorite Button */}
      <button
        onClick={handleFavoriteClick}
        className="absolute top-2 right-2 z-20 w-6 h-6 flex items-center justify-center bg-white border border-gray-200 rounded-full shadow-sm transition-all"
      >
        {isFavorite(product.id)
          ? <FilledHeart style={{ fontSize: '10px' }} className="text-gray-900" />
          : <EmptyHeart style={{ fontSize: '10px' }} className="text-gray-300 group-hover:text-gray-400 transition-colors" />
        }
      </button>

      {/* Image */}
      <div className="w-full bg-gray-50 overflow-hidden" style={{ aspectRatio: '3/4' }}>
        {!imageLoaded && !imageError
          ? <ImagePlaceholder />
          : <img
              src={product.image}
              alt={product.name}
              className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-300"
            />
        }
      </div>

      {/* Price + Button */}
      <div className="bg-white py-2 px-2 flex flex-col items-center gap-1.5">
        <div className="text-center">
          <span className="text-xs font-semibold text-gray-900">৳{product.price}</span>
          {product.originalPrice && product.originalPrice > product.price && (
            <span className="text-xs text-gray-400 line-through ml-1.5">৳{product.originalPrice}</span>
          )}
        </div>

        <button
          onClick={handleAddToCartClick}
          disabled={isOutOfStock}
          className={`w-full py-1.5 text-xs font-medium tracking-wide uppercase transition-colors flex items-center justify-center gap-1 ${
            isOutOfStock
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : inCart
              ? 'bg-gray-700 text-white hover:bg-gray-900'
              : 'bg-gray-900 text-white hover:bg-gray-700'
          }`}
        >
          <FaShoppingCart style={{ fontSize: '10px' }} />
          {isOutOfStock ? 'Out of Stock' : inCart ? 'In Cart' : 'Add to Cart'}
        </button>
      </div>
    </div>
  );
};

// ─── FavoritesPage ───────────────────────────────────────
function FavoritesPage({ toggleFavorite, addToCart, cartItems = [] }) {
  const navigate = useNavigate();
  const [favoritedItems, setFavoritedItems] = useState([]);

  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem('favorites') || '[]');
    setFavoritedItems(saved);
  }, []);

  const isFavorite = (productId) =>
    favoritedItems.some(fav => String(fav.id) === String(productId));

  const handleToggle = (productId) => {
    const saved = JSON.parse(localStorage.getItem('favorites') || '[]');
    const updated = saved.filter(fav => String(fav.id) !== String(productId));
    localStorage.setItem('favorites', JSON.stringify(updated));
    setFavoritedItems(updated);
    if (toggleFavorite) toggleFavorite(productId);
  };

  return (
    <div style={appleFont} className="min-h-screen bg-white">

      {/* Header */}
      <div className="border-b border-gray-100 px-6 py-10 max-w-6xl mx-auto">
        <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase mb-3">Your Saves</p>
        <div className="flex items-end justify-between">
          <h1 className="text-4xl font-semibold text-gray-900 tracking-tight">Wishlist</h1>
          <p className="text-sm text-gray-400 mb-1">
            {favoritedItems.length} {favoritedItems.length === 1 ? 'item' : 'items'}
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10">

        {/* Empty State */}
        {favoritedItems.length === 0 ? (
          <div className="border border-gray-100 py-20 text-center">
            <EmptyHeart className="mx-auto text-gray-200 mb-5 w-10 h-10" />
            <p className="text-base font-medium text-gray-900 mb-1">Your wishlist is empty</p>
            <p className="text-sm text-gray-400 mb-8">Save items you love to buy them later.</p>
            <button
              onClick={() => navigate('/')}
              className="px-8 py-2.5 bg-gray-900 text-white text-sm font-medium hover:bg-gray-700 transition-colors"
            >
              Start Shopping
            </button>
          </div>
        ) : (
          <>
            {/* Product Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {favoritedItems.map((product) => (
                <div key={product.id} className="bg-white border border-gray-100">
                  <ProductCard
                    product={product}
                    addToCart={addToCart}
                    toggleFavorite={handleToggle}
                    isFavorite={isFavorite}
                    cartItems={cartItems}
                    navigate={navigate}
                  />
                </div>
              ))}
            </div>

            {/* Bottom Info */}
            <div className="mt-12 pt-10 border-t border-gray-100 grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  icon: <MdOutlineLocalShipping className="w-5 h-5 text-gray-400" />,
                  title: 'Free Shipping',
                  desc: 'On orders over ৳500'
                },
                {
                  icon: <BsArrowRepeat className="w-5 h-5 text-gray-400" />,
                  title: 'Easy Returns',
                  desc: '30-day return policy'
                },
                {
                  icon: (
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  ),
                  title: 'Secure Payment',
                  desc: '100% secure transactions'
                }
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="flex-shrink-0 mt-0.5">{item.icon}</div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{item.title}</p>
                    <p className="text-sm text-gray-400 mt-0.5">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default FavoritesPage;